/**
 * Abrechnung für einen Creator über einen Zeitraum (Monat, Quartal, Jahr): was die Marken für seine Kooperationen gezahlt
 * haben, was davon der Agentur zusteht, welche Auslagen durchlaufen, was an ihn ausgezahlt wurde und was noch offen ist.
 * Managementverträge sehen eine solche Aufstellung regelmäßig vor. Rein; Beträge in Cent je Währung, nie gemischt.
 */
import { shareOfHonorar, type BrandDiscount, type CommissionTier } from "./kooperationen.js";
import type { Cents, IsoDate } from "./types.js";

export interface StatementBrandPayment {
  /** Rechnung der Agentur an die Marke (Weg „über die Agentur“) */
  invoiceNumber: string | null;
  brand: string;
  dealTitle: string | null;
  paidOn: IsoDate;
  currency: string;
  netCents: Cents;
  /** davon provisionspflichtig (ohne durchlaufende Auslagen) */
  commissionableCents: Cents;
  commissionPercent: number;
  /**
   * Aufteilung aus den Positionen der Rechnung (statementSplit, GELD-20): Provision nach Satz oder Staffel und Abzug der Marke,
   * Agenturgebühr (gehört der Agentur), Auslagen (gehen an den Creator). Ohne Angabe gilt der einfache Satz auf commissionableCents.
   */
  split?: StatementSplit;
}

export interface StatementSplit {
  netCents: Cents;
  commissionCents: Cents;
  feeCents: Cents;
  expensesCents: Cents;
  creatorShareCents: Cents;
}

export interface StatementLine {
  description: string;
  netCents: Cents;
  commissionFree: boolean;
}

/** Position „Agenturgebühr“ auf der Rechnung an die Marke (so benennt sie die Vorbelegung, auch ältere Rechnungen ohne Kennzeichen) */
export function isAgencyFeeLine(description: string): boolean {
  return /^\s*(agenturgeb(ü|ue)hr|agency fee)\b/i.test(description);
}

/**
 * Anteile einer bezahlten Rechnung an die Marke (Weg über die Agentur) für die Abrechnung des Creators (GELD-20): Die Agenturgebühr
 * gehört ganz der Agentur, provisionsfreie Positionen (Auslagen) gehen ganz an den Creator, das Honorar teilt sich nach Satz bzw.
 * Staffel und nach dem Abzug der Marke (Minuszeile „Agenturvergütung der Marke“) wie in Aufteilung und Gutschrift (shareOfHonorar):
 * trägt die Agentur den Abzug, bleibt der Creator-Anteil unberührt. Summenprobe: Provision + Gebühr + Creator-Anteil = netto.
 */
export function statementSplit(lines: readonly StatementLine[], rate: { percent: number; tiers?: readonly CommissionTier[] | null },
  discount?: BrandDiscount | null): StatementSplit {
  const fee = lines.filter((l) => isAgencyFeeLine(l.description));
  const rest = lines.filter((l) => !isAgencyFeeLine(l.description));
  const feeCents = fee.reduce((a, l) => a + l.netCents, 0);
  const expensesCents = rest.filter((l) => l.commissionFree).reduce((a, l) => a + l.netCents, 0);
  const honorar = rest.filter((l) => !l.commissionFree && l.netCents > 0).reduce((a, l) => a + l.netCents, 0);
  const abzug = -rest.filter((l) => !l.commissionFree && l.netCents < 0).reduce((a, l) => a + l.netCents, 0);
  const d: BrandDiscount | null = abzug > 0 && honorar > 0 ? { percent: (abzug * 100) / honorar, bearer: discount?.bearer ?? "shared" } : null;
  const s = shareOfHonorar(honorar, rate, d);
  // Rundung des abgeleiteten Abzugs: der Creator-Anteil schließt die Summe (was die Marke für das Honorar gezahlt hat)
  const creatorHonorar = honorar - abzug - s.agencyCents;
  return { netCents: lines.reduce((a, l) => a + l.netCents, 0), commissionCents: s.agencyCents, feeCents, expensesCents, creatorShareCents: creatorHonorar + expensesCents };
}

export interface StatementPayout {
  /** Rechnung oder Gutschrift des Creators an die Agentur */
  invoiceNumber: string | null;
  paidOn: IsoDate;
  currency: string;
  grossCents: Cents;
}

export interface StatementOpen {
  invoiceNumber: string | null;
  label: string;
  dueDate: IsoDate | null;
  currency: string;
  grossCents: Cents;
}

export interface StatementInput {
  talentName: string;
  from: IsoDate;
  to: IsoDate;
  brandPayments: readonly StatementBrandPayment[];
  payouts: readonly StatementPayout[];
  /** offene Auszahlungen an den Creator (freigegeben, noch nicht bezahlt) */
  openPayouts: readonly StatementOpen[];
  /** Direktweg: Provisionsrechnungen der Agentur an den Creator im Zeitraum */
  commissionInvoices?: readonly { invoiceNumber: string | null; issueDate: IsoDate; currency: string; grossCents: Cents; paid: boolean }[];
  /** Sprache der Zeilentexte (Standard Deutsch); die Agentur-Oberfläche übergibt ihre Sprache */
  language?: "de" | "en";
}

export interface StatementTotals {
  currency: string;
  brandNetCents: Cents;
  commissionCents: Cents;
  /** Agenturgebühr aus den Rechnungen an die Marke (gehört der Agentur, nicht Teil des Creator-Anteils) */
  feeCents: Cents;
  expensesCents: Cents;
  creatorShareCents: Cents;
  paidOutCents: Cents;
  openCents: Cents;
  commissionInvoicedCents: Cents;
}

export interface CreatorStatement {
  talentName: string;
  from: IsoDate;
  to: IsoDate;
  lines: { date: IsoDate; kind: "brand_payment" | "payout" | "commission_invoice"; text: string; currency: string; cents: Cents }[];
  totals: StatementTotals[];
  open: StatementOpen[];
}

const inRange = (d: IsoDate, from: IsoDate, to: IsoDate) => d >= from && d <= to;

export function computeCreatorStatement(input: StatementInput): CreatorStatement {
  const en = input.language === "en";
  const totals = new Map<string, StatementTotals>();
  const tot = (c: string) => {
    let t = totals.get(c);
    if (!t) totals.set(c, (t = { currency: c, brandNetCents: 0, commissionCents: 0, feeCents: 0, expensesCents: 0, creatorShareCents: 0, paidOutCents: 0, openCents: 0, commissionInvoicedCents: 0 }));
    return t;
  };
  const lines: CreatorStatement["lines"] = [];
  for (const p of input.brandPayments) {
    if (!inRange(p.paidOn, input.from, input.to)) continue;
    const t = tot(p.currency);
    const commission = p.split ? p.split.commissionCents : Math.round((p.commissionableCents * p.commissionPercent) / 100);
    const expenses = p.split ? p.split.expensesCents : p.netCents - p.commissionableCents;
    t.brandNetCents += p.netCents;
    t.commissionCents += commission;
    t.feeCents += p.split?.feeCents ?? 0;
    t.expensesCents += expenses;
    t.creatorShareCents += p.split ? p.split.creatorShareCents : p.netCents - commission;
    lines.push({ date: p.paidOn, kind: "brand_payment", currency: p.currency, cents: p.netCents,
      text: en
        ? `Payment ${p.brand}${p.dealTitle ? ` · ${p.dealTitle}` : ""}${p.invoiceNumber ? ` (${p.invoiceNumber})` : ""}: commission ${String(p.commissionPercent)} %`
        : `Zahlung ${p.brand}${p.dealTitle ? ` · ${p.dealTitle}` : ""}${p.invoiceNumber ? ` (${p.invoiceNumber})` : ""}: Provision ${String(p.commissionPercent).replace(".", ",")} %` });
  }
  for (const p of input.payouts) {
    if (!inRange(p.paidOn, input.from, input.to)) continue;
    tot(p.currency).paidOutCents += p.grossCents;
    lines.push({ date: p.paidOn, kind: "payout", currency: p.currency, cents: p.grossCents, text: `${en ? "Payout to the creator" : "Auszahlung an dich"}${p.invoiceNumber ? ` (${p.invoiceNumber})` : ""}` });
  }
  for (const c of input.commissionInvoices ?? []) {
    if (!inRange(c.issueDate, input.from, input.to)) continue;
    tot(c.currency).commissionInvoicedCents += c.grossCents;
    lines.push({ date: c.issueDate, kind: "commission_invoice", currency: c.currency, cents: c.grossCents,
      text: en
        ? `Agency commission invoice${c.invoiceNumber ? ` (${c.invoiceNumber})` : ""}${c.paid ? " · paid" : " · open"}`
        : `Provisionsrechnung der Agentur${c.invoiceNumber ? ` (${c.invoiceNumber})` : ""}${c.paid ? " · bezahlt" : " · offen"}` });
  }
  for (const o of input.openPayouts) tot(o.currency).openCents += o.grossCents;
  lines.sort((a, b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind));
  return { talentName: input.talentName, from: input.from, to: input.to, lines, totals: [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency)), open: [...input.openPayouts] };
}

/** Zeitraum für eine Abrechnung: Monat (YYYY-MM), Quartal (YYYY-Q1…Q4) oder Jahr (YYYY) */
export function statementRange(key: string): { from: IsoDate; to: IsoDate } | null {
  const m = /^(\d{4})(?:-(\d{2})|-Q([1-4]))?$/.exec(key.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const pad = (n: number) => String(n).padStart(2, "0");
  const last = (yy: number, mm: number) => new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  if (m[2]) {
    const mm = Number(m[2]);
    if (mm < 1 || mm > 12) return null;
    return { from: `${y}-${pad(mm)}-01`, to: `${y}-${pad(mm)}-${pad(last(y, mm))}` };
  }
  if (m[3]) {
    const q = Number(m[3]);
    const a = (q - 1) * 3 + 1;
    return { from: `${y}-${pad(a)}-01`, to: `${y}-${pad(a + 2)}-${pad(last(y, a + 2))}` };
  }
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}
